import React, { useEffect, useState } from 'react'
import { User01 } from '@untitled-ui/icons-react'

function readToken() {
  try {
    // Shared across portals: the SAW portal authenticates with `token-saw`, the
    // HR backoffice with `token-backoffice`. Hardcoding `token-backoffice` meant
    // the SAW avatar had no Bearer → the auth-gated media proxy 401'd → the photo
    // never rendered. Read whichever this portal set (SAW-first, HR fallback).
    const pick = (name) => {
      const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'))
      return m ? decodeURIComponent(m[1]) : ''
    }
    return pick('token-saw') || pick('token-backoffice')
  } catch {
    return ''
  }
}

// Uploaded media (avatars/logos/KTP) now sits behind an auth-gated proxy, so a
// plain <img src="/…/uploads/<id>"> can't authenticate and would 401. Detect
// those and fetch them as an authed blob. Everything else — a File upload
// preview, a data:/blob: URL, an external avatar — passes straight through.
function isProtectedMedia(v) {
  return typeof v === 'string' && v.includes('/uploads/')
}

// Origins allowed to receive the Bearer token, read from the same runtime config
// the app uses (window.__APP_CONFIG__, Vite env as the local-dev fallback):
//   1. UPLOADS_BASE_URL — the host that serves /uploads. The SAW portal's API is a
//      different origin from the HR backoffice-service that holds the media, so
//      SAW sets this; HR doesn't and falls through to its own API.
//   2. API_BASE_URL — the portal's own API.
// The first one is where relative "/…/uploads/<id>" paths resolve. Nothing else
// ever gets the token: an avatar/logo URL comes from the DB, and a tenant-planted
// "https://attacker.tld/uploads/x.png" would otherwise receive the viewer's
// access token (cross-tenant for internal users).
//
// VITE_UPLOADS_BASE_URL is honoured only under `vite dev` (mirrors SAW's
// runtimeConfig): images are built from a workspace .env that carries the DEV
// uploads host, so a deployed env that doesn't inject UPLOADS_BASE_URL (staging)
// would otherwise trust — and send its token to — the dev API.
function mediaOrigins() {
  let uploads = ''
  let api = ''
  try {
    const cfg = (typeof window !== 'undefined' && window.__APP_CONFIG__) || {}
    uploads =
      cfg.UPLOADS_BASE_URL ||
      (import.meta.env.DEV ? import.meta.env.VITE_UPLOADS_BASE_URL : '') ||
      ''
    api = cfg.API_BASE_URL || import.meta.env.VITE_API_BASE_URL || ''
  } catch {
    uploads = ''
    api = ''
  }
  const bases = [uploads, api].filter(Boolean)
  // No base configured at all: the API sits behind the page's own origin.
  if (!bases.length) bases.push('')
  const origins = []
  for (const base of bases) {
    try {
      const origin = new URL(base, window.location.origin).origin
      if (!origins.includes(origin)) origins.push(origin)
    } catch {
      // unparseable base: contributes no origin
    }
  }
  return origins
}

// Resolve a protected media path to the absolute URL to fetch with the token, or
// null when it points at an origin outside mediaOrigins(). Relative paths go to
// the uploads/API origin; absolute ones keep their own origin and must match.
function resolveMediaUrl(u) {
  const origins = mediaOrigins()
  if (!origins.length) return null
  let url
  try {
    url = /^https?:\/\//i.test(u)
      ? new URL(u)
      : new URL(`${origins[0]}${u.startsWith('/') ? u : `/${u}`}`)
  } catch {
    return null
  }
  return origins.includes(url.origin) ? url.href : null
}

function MyAvatar({
  photo,
  size = 40,
  iconSize = 24,
  showOnlineStatus = false,
}) {
  const [isPhotoError, setIsPhotoError] = useState(false)
  const [resolvedSrc, setResolvedSrc] = useState(null)

  useEffect(() => {
    setIsPhotoError(false)
    setResolvedSrc(null)
    if (!photo) return undefined

    // File / Blob upload preview.
    if (typeof photo !== 'string') {
      const objectUrl = URL.createObjectURL(photo)
      setResolvedSrc(objectUrl)
      return () => URL.revokeObjectURL(objectUrl)
    }

    // Non-protected string (data:/blob:/external) — use as-is.
    if (!isProtectedMedia(photo)) {
      setResolvedSrc(photo)
      return undefined
    }

    // Protected media on a foreign origin — never send the token there. Show the
    // placeholder icon (same as a photo that fails to load) without requesting it.
    const url = resolveMediaUrl(photo)
    if (!url) {
      setIsPhotoError(true)
      return undefined
    }

    // Protected media — fetch with the auth header, render the blob.
    let active = true
    let objectUrl = null
    const controller = new AbortController()
    fetch(url, {
      headers: { Authorization: `Bearer ${readToken()}` },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error(String(res.status))
        return res.blob()
      })
      .then((blob) => {
        if (!active) return
        objectUrl = URL.createObjectURL(blob)
        setResolvedSrc(objectUrl)
      })
      .catch((err) => {
        if (active && err?.name !== 'AbortError') setIsPhotoError(true)
      })
    return () => {
      active = false
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [photo])

  const showImg = resolvedSrc && !isPhotoError

  return (
    <div className="relative">
      <div
        style={{
          position: '',
          height: size,
          width: size,
          minHeight: size,
          minWidth: size,
        }}
        className="overflow-hidden rounded-full border border-[#00000014]"
      >
        {showImg ? (
          <img
            className="h-full w-full object-cover"
            src={resolvedSrc}
            alt=""
            onError={() => {
              setIsPhotoError(true)
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gray-light/100">
            <User01
              className={`size-${iconSize >= 96 ? '24' : '13'} text-gray-light/500`}
              stroke="currentColor"
            />
          </div>
        )}
        {showOnlineStatus && (
          <div className="absolute bottom-0 right-0 h-3 min-h-[12px] w-3 min-w-[12px] rounded-full border border-white bg-success/500" />
        )}
      </div>
    </div>
  )
}

export default MyAvatar
